<template>
  <div class="stories-list">
    <div class="grid" :class="{
      'grid-skeleton': loading,
      'grid-three-or-more': stories.length >= 3,
      'grid-less-than-three': stories.length > 0 && stories.length < 3
    }">
      <div v-if="loading">
        <StoryCardSkeleton v-for="n in 10" :key="n" />
      </div>
      <draggable v-else-if="stories.length" v-model="stories" v-bind="dragOptions" @start="drag = true"
        @end="sortStories()" tag="div">
        <transition-group>
          <StoryCard v-for="story in stories"
          :key="story.id"
          :storyInfo="story"
          :editPermission="editPermission"
          :languages="languages"
          :isViewscreen="isViewscreen"
          :isMediaChecked="storiesToViewscreenArray.some(item => item.id === story.context.id)"
          :displays="displays"
          @addToMediaArray="addToMediaArray(story.context.id)"
          @deleteFromMediaArray="deleteFromMediaArray(story.context.id)"
          @openFullInfo="openStoryModal(story)"
          @openEdit="openStoryPage(story.context.context.id)" />
        </transition-group>
      </draggable>
      <EmptyState v-else :text="emptyStateText" :isAddDisabled="false" textBtn="Add" @add="createStory" />
    </div>
    <a-modal v-model="isStoryModalOpen" :footer="null" width="920px" :destroyOnClose="true">
      <template #title>
        <div>
          <span style="display: block;">Story info</span>
          <span class="modal-uuid">UUID: {{ storyUUID }}</span>
        </div>
      </template>

      <StoryInfoModal :storyInfo="storyObject" :languages="languages" :displays="displays" @close="closeStoryModal" />
    </a-modal>
  </div>
</template>
<script>
import StoryCard from '@/views/apps/media/components/stories/storyCard.vue'
import StoryCardSkeleton from '@/views/apps/media/components/stories/storyCardSkeleton.vue'
import StoryInfoModal from '@/views/apps/media/components/stories/storyInfoModal.vue'
import EmptyState from '@/components/custom/emptyState.vue'

import MediaService from '@/services/api/apiMediaService'
// import ViewscreenService from '@/services/api/apiViewscreenService'
import { routerPushWithFilters } from '@/services/utils/routerHelpers'

import draggable from 'vuedraggable'
export default {
  name: 'Stories-list',
  components: {
    draggable,
    StoryCard,
    StoryCardSkeleton,
    StoryInfoModal,
    EmptyState,
  },
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
    languages: {
      type: Array,
    },
    filters: {
      type: Object,
    },
    isAddAllChecked: {
      type: Boolean,
      default: false,
    },
    isViewscreen: {
      type: Boolean,
      default: false,
    },
    mediaToViewscreenArray: {
      type: Array,
    },
    displays: {
      type: Object,
    },
    filtersReady: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isInternalCheckboxChange: false,
      stories: [],
      drag: false,
      loading: false,
      apps: undefined,
      isStoryModalOpen: false,
      storyObject: undefined,
      storiesToViewscreenArray: [],
    }
  },
  computed: {
    storyUUID() {
      return this.storyObject ? this.storyObject.context.context.uuid : ''
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    emptyStateText() {
      return this.filters.name?.length ? 'Oops! Nothing matches your search.' : 'Add first story'
    },
    filtersToBeWatched() {
      return Object.assign({}, this.filters)
    },
  },
  async created() {
    await this.listUpdate()
    const filteredStories = this.mediaToViewscreenArray.filter(item => item.type === 'story')
    this.storiesToViewscreenArray = filteredStories

    filteredStories.length === this.stories.length ? this.$emit('changeAllChecked', true) : this.$emit('changeAllChecked', false)
  },
  watch: {
    filtersToBeWatched: {
      handler(newVal, oldVal) {
        if (!this.filtersReady) return

        if (!newVal.search && !oldVal.search && newVal.selectedSearch !== oldVal.selectedSearch) {
          return
        }

        this.listUpdate()
      },
      deep: true,
    },
    isAddAllChecked(newValue) {
      if (newValue) {
        const nonDuplicateIds = this.stories
          .map(story => story.context.id)
          .filter(storyId => !this.storiesToViewscreenArray.some(item => item.id === storyId))

        this.storiesToViewscreenArray.push(
          ...nonDuplicateIds.map(id => ({ id, type: 'story' })),
        )
      } else {
        this.$emit('mediaToSplice', this.storiesToViewscreenArray)
        this.storiesToViewscreenArray = []
      }
      this.$emit('changeMediaArray', this.storiesToViewscreenArray)
    },
  },
  methods: {
    addToMediaArray(storyId) {
      this.storiesToViewscreenArray.push({ id: storyId, type: 'story' })
      this.$emit('changeMediaArray', this.storiesToViewscreenArray)
      if (this.storiesToViewscreenArray.length === this.stories.length) {
        this.$emit('changeAllChecked', true)
      }
    },
    deleteFromMediaArray(storyId) {
      const deletedIndex = this.storiesToViewscreenArray.findIndex(item => item.id === storyId && item.type === 'story')

      if (deletedIndex !== -1) {
        this.storiesToViewscreenArray.splice(deletedIndex, 1)
        this.$emit('mediaToSplice', storyId, 'story')
        console.log(storyId)
        this.$nextTick(() => {
          this.isInternalCheckboxChange = true
          this.$emit('changeAllChecked', false)
        })
      }
    },
    openStoryPage(id) {
      routerPushWithFilters(this.$router, `/media/stories/edit/${id}`)
    },
    createStory() {
      routerPushWithFilters(this.$router, '/media/stories/create')
    },
    redirectToMain() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    openStoryModal(storyObject) {
      this.storyObject = storyObject
      this.isStoryModalOpen = true
    },
    closeStoryModal() {
      this.isStoryModalOpen = false
    },
    async listUpdate() {
      try {
        this.loading = true
        await this.getStories()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async getStories() {
      const { search, active, localizationId, applicationId, selectedSearch } = this.filters
      const searchParams = new URLSearchParams()
      searchParams.append('active', active ? '1' : '0')

      if (typeof localizationId !== 'undefined') {
        searchParams.append('lang_id', localizationId)
      }

      if (typeof applicationId !== 'undefined') {
        searchParams.append('app_id', applicationId)
      }

      if (search !== undefined) {
        searchParams.append(selectedSearch, search)
      }

      if (this.isViewscreen) {
        searchParams.append('section_id', this.$route.params.sectionId)
      }

      try {
        this.loading = true

        const storiesResponse = await MediaService.getStories(searchParams)

        if (!storiesResponse?.data?.data) {
          this.$notification.error({
            message: 'Something went wrong. Please try again later.',
          })
        }

        this.stories = storiesResponse.data.data
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      } finally {
        this.loading = false
      }
    },

    async sortStories() {
      const storiesSort = this.stories.map((story, index, storiesArr) => {
        return {
          id: story.context.context.id,
          sort: storiesArr.length - index,
        }
      })
      try {
        await MediaService.sortStories(this.filters.applicationId, storiesSort)
        this.$notification.success({
          message: 'Sucessfully sorted',
        })
      } catch (error) {
        console.log(error)
      }
      this.drag = false
    },
  },
}
</script>
